/* eslint-disable complexity */
import {
    DateFormats,
    formatDate,
    formatGender,
    formatPhoneNumber,
    getAge,
} from '@strata/helper-functions';
import { EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
} from '@strata/react-label-value-pairs';
import { InformationIcon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
    getMemberEligibilityStatusDisplay,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface MemberDetailsProps {
    referralDetails?: ReferralDetails;
}

export const MemberDetails = ({ referralDetails = undefined }: MemberDetailsProps) => {
    const { payersEnhancedEligibility } = useFlags<{ payersEnhancedEligibility: boolean }>();
    const {
        memberDetails,
        isFetching: isMemberDetailsFetching,
        willBeUnmanagedOn,
    } = useScopeAssessorMemberDetails(referralDetails, { computeWillBeUnmanagedOn: true });

    const eligibilityDisplay = memberDetails
        ? getMemberEligibilityStatusDisplay(memberDetails.enrollmentStatus)
        : null;
    const showWillBeUnmanagedOn =
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING && !!willBeUnmanagedOn;

    return (
        <FormSectionWithRows
            sectionTitle="Member Details"
            isLoading={isMemberDetailsFetching}
            hoverOverlayText="Member details are hidden. Hover to view information."
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Gender"
                        value={formatGender(memberDetails?.gender)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Age"
                        value={getAge(memberDetails?.dateOfBirth)}
                    />
                    <DetailLabelValuePair
                        label="DOB"
                        value={formatDate(memberDetails?.dateOfBirth)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelList
                        label="Phone"
                        style={{ width: '33%' }}
                        values={[
                            formatPhoneNumber(memberDetails?.phone),
                            ...(memberDetails?.secondaryPhone
                                ? [
                                      // eslint-disable-next-line react/jsx-key
                                      <div
                                          className="flex__gap--x-small"
                                          style={{ alignItems: 'center' }}
                                      >
                                          <span>
                                              {formatPhoneNumber(memberDetails.secondaryPhone)}
                                          </span>
                                          <InformationIcon tooltipText="Payer-submitted supplementary phone number" />
                                      </div>,
                                  ]
                                : []),
                        ]}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Email"
                        value={<MailToHyperlink emailAddress={memberDetails?.email} />}
                    />
                    <AddressDetailLabelValue
                        label="Address"
                        address1={memberDetails?.addressLine1}
                        address2={memberDetails?.addressLine2}
                        city={memberDetails?.city}
                        state={memberDetails?.state}
                        zip={memberDetails?.zip}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Group Name"
                        value={memberDetails?.benefitGroupName}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Group ID"
                        value={memberDetails?.benefitGroup}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Carrier"
                        value={memberDetails?.carrier}
                    />
                </>
            }
            rowFourContent={
                <DetailLabelValuePair
                    style={{ width: '33%' }}
                    label="Account"
                    value={memberDetails?.account}
                />
            }
            rowFiveContent={
                payersEnhancedEligibility && eligibilityDisplay ? (
                    <>
                        <div style={{ width: '33%' }}>
                            <DetailLabel label="Enrollment Status" />
                            <StatusIndicator
                                message={eligibilityDisplay.label}
                                status={eligibilityDisplay.status}
                                tooltipText={eligibilityDisplay.tooltipText}
                            />
                        </div>
                        <DetailLabelValuePair
                            style={{ width: '33%' }}
                            label="Enrollment change date"
                            value={formatDate(memberDetails?.lastUpdated, DateFormats.DATE_TIME)}
                        />
                        {showWillBeUnmanagedOn ? (
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Grace period ends"
                                value={formatDate(willBeUnmanagedOn)}
                            />
                        ) : (
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Coverage dates"
                                value={
                                    <EnrollmentCoverageDates
                                        enrollmentStart={memberDetails?.enrollmentStart}
                                        enrollmentEnd={memberDetails?.enrollmentEnd}
                                        isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                                    />
                                }
                            />
                        )}
                    </>
                ) : undefined
            }
            rowSixContent={
                payersEnhancedEligibility && eligibilityDisplay && showWillBeUnmanagedOn ? (
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Coverage dates"
                        value={
                            <EnrollmentCoverageDates
                                enrollmentStart={memberDetails?.enrollmentStart}
                                enrollmentEnd={memberDetails?.enrollmentEnd}
                                isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                            />
                        }
                    />
                ) : undefined
            }
        />
    );
};
